import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MDBDataTable } from 'mdbreact';
import Select from 'react-select';
import { useHistory } from 'react-router';
import ActionDropdown from './ActionDropdown';
import { STATUS_OPTIONS, QUOTE_TYPES, selectMenuStyles } from '../../helpers/quotes';
import { getQuoteBuilderURL, quoteStatusLabel, quoteStatusBadge, formatTimestamp } from '../../helpers/common';

const COLUMNS = [
  { label: ['Quote No.', <i key="proposal-number" className="fa fa-sort ml-2"></i>], field: 'ProposalNumber', sort: 'asc' },
  { label: ['Company Name', <i key="company-name" className="fa fa-sort ml-2"></i>], field: 'CompanyName', sort: 'asc' },
  { label: ['Description', <i key="description" className="fa fa-sort ml-2"></i>], field: 'Description', sort: 'asc' },
  { label: ['Status', <i key="status" className="fa fa-sort ml-2"></i>], field: 'Status', sort: 'asc' },
  { label: ['Quote 1 Type', <i key="qt1" className="fa fa-sort ml-2"></i>], field: 'QuoteType1', sort: 'asc' },
  { label: ['Quote 2 Type', <i key="qt2" className="fa fa-sort ml-2"></i>], field: 'QuoteType2', sort: 'asc' },
  { label: ['Last Updated', <i key="last-updated" className="fa fa-sort ml-2"></i>], field: 'LastUpdated', sort: 'asc' },
  { label: 'Action', field: 'Action', sort: 'disabled' },
];

const QuotesTable = ({ quotes, refetch }) => {
  const history = useHistory();
  const [tableData, setTableData] = useState(null);
  const statusFilter = useRef(null);
  const quoteType1Filter = useRef(null);
  const quoteType2Filter = useRef(null);

  const filterData = useCallback(() => {
    if (!quotes) return;
    let filtered = quotes;
    if (statusFilter.current) {
      filtered = filtered.filter((q) => q.Status === statusFilter.current);
    } else {
      filtered = filtered.filter((q) => q.Status !== 'archived');
    }
    if (quoteType1Filter.current) filtered = filtered.filter((q) => q.QuoteTypeId1 === quoteType1Filter.current);
    if (quoteType2Filter.current) filtered = filtered.filter((q) => q.QuoteTypeId2 === quoteType2Filter.current);

    const rows = filtered
      .slice()
      .map((quote) => ({
        ProposalNumber: quote.ProposalNumber ?? '',
        CompanyName: quote.CompanyName ?? '',
        Description: quote.Description ?? '',
        Status: <span searchvalue={quoteStatusLabel(quote.Status)}>{quoteStatusBadge(quote.Status)}</span>,
        QuoteType1: quote.QuoteType1?.Name ?? '',
        QuoteType2: quote.QuoteType2?.Name ?? '',
        LastUpdated: <span searchvalue={quote.ModifiedTimestamp}>{formatTimestamp(quote.ModifiedTimestamp)}</span>,
        Action: <ActionDropdown quote={quote} refetch={refetch} />,
        clickEvent: () => history.push(getQuoteBuilderURL(quote.WorkflowStep, quote.QuoteId)),
        Created: quote.Created,
      }));

    setTableData({ columns: COLUMNS, rows });
  }, [quotes, refetch, history]);

  useEffect(() => {
    if (quotes) filterData();
  }, [quotes, filterData]);

  const handleStatusChange = (e) => { statusFilter.current = e?.value ?? null; filterData(); };
  const handleTypeChange = (e, type) => {
    if (type === 'QuoteType1') quoteType1Filter.current = e?.value ?? null;
    else quoteType2Filter.current = e?.value ?? null;
    filterData();
  };

  return (
    <>
      <h6 className="mt-0">Filter Quotes</h6>
      <div className="quote-filters">
        <Select options={STATUS_OPTIONS} placeholder="Status" styles={selectMenuStyles} isClearable onChange={handleStatusChange} />
        <Select options={QUOTE_TYPES} placeholder="Quote 1 Type" styles={selectMenuStyles} isClearable onChange={(e) => handleTypeChange(e, 'QuoteType1')} />
        <Select options={QUOTE_TYPES} placeholder="Quote 2 Type" styles={selectMenuStyles} isClearable onChange={(e) => handleTypeChange(e, 'QuoteType2')} />
      </div>
      {tableData && (
        <MDBDataTable
          className="quotes-list-table"
          sortable
          striped
          borderless
          theadColor="table-dark"
          hover
          responsiveSm
          noBottomColumns
          noRecordsFoundLabel="No quotes found"
          data={tableData}
          sortRows={['LastUpdated', 'Status']}
        />
      )}
    </>
  );
};

export default QuotesTable;
